<template>
  <div class="surface-panels">
    <div v-if="xCurtain" class="curtain is-animated fade-in" />
    <UserMenu
      v-if="xUserMenu"
      :dir="dir"
      :user="user"
      @upgrade="userTriggeredUpgrade({ user: user, upgradeSource: UpgradeSource.SurfaceProfileClick })"
    />
    <SurfaceContributors v-if="xContributorsList" />
    <SurfaceFilterModal v-if="xFilterModal" />
    <SurfaceSearchBar
      v-if="!isApp && isSmallerThanTabletLandscape && xSearchBar"
      class="fixed bottom-0 left-0 right-0"
      :should-animate-on-appear="true"
    />
    <SurfacePanelMapLocationPicker v-if="isPickingLocation && !isAppOpeningNativeLocationPicker" />
    <SurfaceSettingsPanel />
    <surface-panel-settings-modal-confirm v-if="xSettingsPanelModalConfirm" @hide="hideSettingsPanelModalConfirm" />
    <surface-panel-share v-if="xSharePanel" />
    <surface-panel-remake v-if="xRemakePanel" />
    <surface-panel-convert-layout v-if="xConvertLayoutPanel" />
    <surface-panel-comments v-if="xCommentsPanel" :post-id="commentsPanelPostId" />
    <SurfacePanelPostExpanded
      v-if="xExpandedPostPanel"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
    <surface-panel-post-action-menu-new v-if="postUnderActionCid && !isNativeMenu" :post-cid="postUnderActionCid" />
    <surface-panel-section-action-menu v-if="xSectionMenu" :section-id="actionMenuSectionId" />
    <surface-panel-comment-action-menu
      v-if="xCommentMenu"
      :comment-id="actionMenuCommentId"
      :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
    />
    <surface-panel-transfer-post
      is="surface-panel-copy-or-transfer-post"
      v-if="xTransferPostPanel"
      :post-cid="postBeingTransferredCid"
      action="transfer"
      @navigate="navigateTransferPanel"
      @close="endPostTransfer"
    />
    <surface-panel-copy-post
      is="surface-panel-copy-or-transfer-post"
      v-if="xCopyPostPanel"
      :post-cid="postBeingCopiedCid"
      action="copy"
      @navigate="navigateCopyPanel"
      @close="endPostCopy"
    />
    <surface-panel-composer-modal v-if="xNewComposerModalPanel" />
    <surface-info v-if="xInfo" @close="hideInfo" />
    <!-- Aug 2021: replaced upgrade-required with `MembershipTierUpgradeModal` however not removed because potentially still used by very old app versions. -->
    <upgrade-required v-if="xWallQuotaExceeded" reason="walls" @close="hideWallQuotaExceeded" />
    <MembershipTierUpgradeModal
      v-if="xUpgradeModal"
      :user="user"
      :file-that-exceeded-size="fileThatExceededSize"
      @cancel="closePlanUpgradeModal"
    />
    <MembershipLibraryTierUpgradeModal
      v-if="xLibraryUpgradeModal"
      :file-that-exceeded-size="fileThatExceededSize"
      @cancel="closeLibraryUpgradeModal"
    />
    <app-upsell-banner v-if="xAppUpsell" @close="hideAppUpsell" />
    <app-update-banner v-if="xAppUpdateRequired" />
    <template v-if="!isAppUsing('accumulatedReactions')">
      <surface-dropdown-reaction-star-new
        v-if="xReactionDropdownStar"
        :dir="dir"
        :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
      />
      <surface-dropdown-reaction-grade-new
        v-if="xReactionDropdownGrade"
        :dir="dir"
        :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
      />
    </template>
    <template v-if="isAppUsing('accumulatedReactions')">
      <SurfaceDropdownReactionGrade :dir="dir" :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'" />
      <SurfaceDropdownReactionStar :dir="dir" :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'" />
    </template>
    <surface-dialog-extension-required v-if="xExtensionRequired" @close="hideExtensionRequired" />
    <canvas-connection-snackbar
      v-if="xConnectionCancellation || xDisconnectionCancellation"
      :x-connection-cancellation="xConnectionCancellation"
      :x-disconnection-cancellation="xDisconnectionCancellation"
      @close="hideCanvasConnection"
    />
    <!-- Permit notifications blocks DOM in E2E test -->
    <dialog-permit-notification v-if="xPermitNotification && !isCypress" @close="hidePermitNotification" />
    <reload-notification v-if="xReloadNotification" @cancel="hideReloadNotification" />
    <image-picker-sources v-if="xImagePickerSources" />
    <SurfaceEditBookmarkModal />
    <GlobalAlertDialog />
    <GlobalInputDialog />
    <GlobalConfirmationDialog />
    <SurfaceSectionRenameModal v-if="xSectionRenameModal && canUseSections" />
    <SurfaceCommentEditModal v-if="xCommentEditModal" />
    <SurfaceActivityPanel v-if="xActivityPanel" />
    <SurfaceSectionActionMenu v-if="sectionUnderActionId" />
    <SurfaceReportPanel v-if="xReportPanel" />
  </div>
</template>

<script>
import appCan from 'bits/app_can'
import { isAppUsing } from 'bits/flip'
import { defineAsyncComponent } from 'bits/vue'
import { mapState as piniaMapState } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfaceFilterStore } from 'pinia/surface_filter'
import { useSurfaceSectionsStore } from 'pinia/surface_sections'
import { mapActions, mapGetters, mapState } from 'vuex'
import { UpgradeSource } from 'vuexstore/modules/plans'

export default {
  // IMPORTANT: If you're adding a new panel here, please also add it to the
  // `isOverlayVisible` getter in `app/javascript/vuexstore/surface/index.ts`
  components: {
    AppUpsellBanner: defineAsyncComponent(() => import('vuecomponents/app_upsell_banner')),
    AppUpdateBanner: defineAsyncComponent(() => import('vuecomponents/app_update_banner')),
    SurfaceContributors: defineAsyncComponent(() => import('vuecomponents/surface_contributors.vue')),
    SurfaceFilterModal: defineAsyncComponent(() => import('vuecomponents/SurfaceFilterModal.vue')),
    SurfaceSearchBar: defineAsyncComponent(() => import('vuecomponents/SurfaceSearchBar.vue')),
    SurfacePanelMapLocationPicker: defineAsyncComponent(() =>
      import('vuecomponents/SurfacePanelMapLocationPicker.vue'),
    ),
    SurfaceSettingsPanel: defineAsyncComponent(() => import('vuecomponents/SurfaceSettingsPanel.vue')),
    SurfacePanelSettingsModalConfirm: defineAsyncComponent(() =>
      import('vuecomponents/surface_panel_settings_modal_confirm'),
    ),
    SurfacePanelShare: defineAsyncComponent(() => import('vuecomponents/surface_panel_share')),
    SurfacePanelRemake: defineAsyncComponent(() => import('vuecomponents/surface_panel_remake')),
    SurfacePanelConvertLayout: defineAsyncComponent(() => import('vuecomponents/surface_panel_convert_layout')),
    SurfacePanelComments: defineAsyncComponent(() => import('vuecomponents/surface_panel_comments')),
    SurfacePanelPostExpanded: defineAsyncComponent(() => import('vuecomponents/SurfacePanelPostExpanded')),
    SurfacePanelPostActionMenuNew: defineAsyncComponent(() => import('vuecomponents/SurfacePanelPostActionMenu')),
    SurfacePanelSectionActionMenu: defineAsyncComponent(() => import('vuecomponents/SurfacePanelSectionActionMenu')),
    SurfaceSectionActionMenu: defineAsyncComponent(() => import('vuecomponents/SurfaceSectionActionMenu.vue')),
    SurfacePanelCommentActionMenu: defineAsyncComponent(() => import('vuecomponents/SurfacePanelCommentActionMenu')),
    SurfacePanelCopyOrTransferPost: defineAsyncComponent(() =>
      import('vuecomponents/surface_panel_copy_or_transfer_post'),
    ),
    SurfacePanelComposerModal: defineAsyncComponent(() => import('vuecomponents/surface_panel_composer_modal')),
    UpgradeRequired: defineAsyncComponent(() => import('vuecomponents/upgrade_required')),
    SurfaceDropdownReactionStar: defineAsyncComponent(() => import('vuecomponents/SurfaceDropdownReactionStar.vue')),
    SurfaceDropdownReactionStarNew: defineAsyncComponent(() =>
      import('vuecomponents/surface-dropdown-reaction-star-new.vue'),
    ),
    SurfaceDropdownReactionGradeNew: defineAsyncComponent(() =>
      import('vuecomponents/surface-dropdown-reaction-grade-new.vue'),
    ),
    SurfaceDropdownReactionGrade: defineAsyncComponent(() => import('vuecomponents/SurfaceDropdownReactionGrade.vue')),
    CanvasConnectionSnackbar: defineAsyncComponent(() => import('vuecomponents/canvas_connection_snackbar')),
    DialogPermitNotification: defineAsyncComponent(() => import('vuecomponents/dialog_permit_notification')),
    SurfaceInfo: defineAsyncComponent(() => import('vuecomponents/surface_info')),
    SurfaceDialogExtensionRequired: defineAsyncComponent(() =>
      import('vuecomponents/surface_dialog_extension_required'),
    ),
    GlobalConfirmationDialog: defineAsyncComponent(() => import('vuecomponents/GlobalConfirmationDialog.vue')),
    GlobalInputDialog: defineAsyncComponent(() => import('vuecomponents/GlobalInputDialog.vue')),
    GlobalAlertDialog: defineAsyncComponent(() => import('vuecomponents/GlobalAlertDialog.vue')),
    ReloadNotification: defineAsyncComponent(() => import('vuecomponents/reload_notification')),
    ImagePickerSources: defineAsyncComponent(() => import('vuecomponents/image_picker_sources')),
    SurfaceSectionRenameModal: defineAsyncComponent(() => import('vuecomponents/SurfaceSectionRenameModal.vue')),
    SurfaceCommentEditModal: defineAsyncComponent(() => import('vuecomponents/SurfaceCommentEditModal.vue')),
    MembershipTierUpgradeModal: defineAsyncComponent(() => import('vuecomponents/MembershipTierUpgradeModal.vue')),
    SurfaceEditBookmarkModal: defineAsyncComponent(() => import('vuecomponents/SurfaceEditBookmarkModal.vue')),
    MembershipLibraryTierUpgradeModal: defineAsyncComponent(() =>
      import('vuecomponents/MembershipLibraryTierUpgradeModal.vue'),
    ),
    UserMenu: defineAsyncComponent(() => import('vuecomponents/UserMenu.vue')),
    SurfaceActivityPanel: defineAsyncComponent(() => import('vuecomponents/SurfaceActivityPanel.vue')),
    SurfaceReportPanel: defineAsyncComponent(() => import('vuecomponents/SurfaceReportPanel.vue')),
  },
  props: {
    reactionIdsByPostId: { type: Object, default: () => ({}) },
    commentIdsByPostId: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      appUpsellClosed: false,
      xSettingsPanelModalConfirm: false,
      UpgradeSource,
    }
  },
  computed: {
    ...mapState([
      'isEmbedded',
      'isApp',
      'isIOS',
      'isAndroid',
      'amIRegistered',
      'isReadOnly',
      'xInfo',
      'xWallQuotaExceeded',
      'xExtensionRequired',
      'fileThatExceededSize',
      'xRemakePanel',
      'xConvertLayoutPanel',
      'xCommentsPanel',
      'xContributorsList',
      'commentsPanelPostId',
      'modalPostEdit',
      'nativePostEdit',
      'isNativeMenu',
      'xPermitNotification',
      'xReloadNotification',
      'wall',
      'dir',
      'isFirstView',
    ]),
    ...mapState('appBarAccount', ['xUserMenu']),
    ...mapGetters('map', ['isPickingLocation']),
    ...mapGetters('plans', ['xUpgradeModal']),
    ...mapGetters('libraryPlans', { xLibraryUpgradeModal: 'xUpgradeModal' }),
    ...mapGetters('post', ['postBeingEditedCid']),
    ...mapGetters('postAction', [
      'zoomedPostCid',
      'expandedPost',
      'expandedPostCid',
      'postUnderActionCid',
      'xTransferPostPanel',
      'postBeingTransferredCid',
      'xCopyPostPanel',
      'postBeingCopiedCid',
    ]),
    ...piniaMapState(useSurfaceStore, ['xSearchBar']),
    ...piniaMapState(useSurfaceFilterStore, ['xFilterModal']),
    ...mapState('comment', ['actionMenuCommentId', 'activeNewCommentPostId', 'commentBeingEditedId']),
    ...mapGetters('comment', ['isCommenting']),
    ...mapState('section', ['actionMenuSectionId']),
    ...piniaMapState(useSurfaceSectionsStore, ['sectionUnderActionId', 'xSectionRenameModal']),
    ...mapGetters('postConnection', ['xConnectionCancellation', 'xDisconnectionCancellation']),
    ...mapGetters('imagePicker', ['xImagePickerSources']),
    ...mapGetters('reaction', ['xReactionDropdownStar', 'xReactionDropdownGrade']),
    ...mapGetters('browser', ['isPhone', 'isSmallerThanTabletLandscape']),
    ...mapGetters([
      'canUseSections',
      'user',
      'dir',
      'canIWrite',
      'xSharePanel',
      'xSettingsPanel',
      'xNewComposerModalPanel',
      'uploadLimit',
      'xReportPanel',
    ]),
    ...mapGetters('activityPanel', ['xActivityPanel']),
    isAppOpeningNativeLocationPicker() {
      return this.isApp && appCan('useNativeMapLocationPicker')
    },
    xAppUpsell() {
      return (
        !this.appUpsellClosed && this.amIRegistered && !this.isEmbedded && (this.isIOS || this.isAndroid) && !this.isApp
      )
    },
    xAppUpdateRequired() {
      // this will show a banner in the mobile app prompting the user to update.
      // return true if this.isApp && appUpdateRequired
      return false
    },
    xCurtain() {
      return this.xSharePanel || this.xCopyPostPanel || this.xTransferPostPanel
    },
    xCommentMenu() {
      return this.actionMenuCommentId && !this.isNativeMenu
    },
    xSectionMenu() {
      return this.actionMenuSectionId && !this.isNativeMenu
    },
    xExpandedPostPanel() {
      return !!this.expandedPostCid && !this.isApp
    },
    xCommentEditModal() {
      return this.isPhone && ((this.isAddingNewComment && !this.xExpandedPostPanel) || this.isEditingComment)
    },
    isAddingNewComment() {
      return !!this.activeNewCommentPostId
    },
    isEditingComment() {
      return !!this.commentBeingEditedId
    },
    isCypress() {
      return !!window.Cypress
    },
  },
  mounted() {
    if (this.isFirstView) {
      this.showSettingsPanel()
    }
  },
  methods: {
    ...mapActions([
      'hideExtensionRequired',
      'hideWallQuotaExceeded',
      'hideInfo',
      'hidePermitNotification',
      'hideReloadNotification',
    ]),
    ...mapActions('plans', ['userTriggeredUpgrade', 'closePlanUpgradeModal']),
    ...mapActions('libraryPlans', { closeLibraryUpgradeModal: 'closeUpgradeModalAndResetPlans' }),
    ...mapActions('postAction', ['postTransferNavigate', 'endPostTransfer', 'postCopyNavigate', 'endPostCopy']),
    ...mapActions('postConnection', ['cancelConnection', 'cancelDisconnection']),
    ...mapActions('confirmationDialog', ['openConfirmationDialog']),
    ...mapActions('settings', ['showSettingsPanel']),
    navigateCopyPanel(panel) {
      this.postCopyNavigate({ panel })
    },
    navigateTransferPanel(panel) {
      this.postTransferNavigate({ panel })
    },
    hideAppUpsell() {
      this.appUpsellClosed = true
    },
    hideCanvasConnection() {
      if (this.xConnectionCancellation) {
        this.cancelConnection()
      } else if (this.xDisconnectionCancellation) {
        this.cancelDisconnection()
      }
    },
    showSettingsPanelModalConfirm() {
      this.xSettingsPanelModalConfirm = true
    },
    hideSettingsPanelModalConfirm() {
      this.xSettingsPanelModalConfirm = false
    },
    isAppUsing,
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/3/modules/all';
.surface-panels {
  .curtain {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    @include vh100;
    z-index: $sidepanel-zindex - 1;
    background-color: $translucent-backdrop-color;
    -webkit-overflow-scrolling: touch;
    touch-action: manipulation;
    @include respondToTouch {
      cursor: pointer;
    }
  }
}
</style>
